import {
  useGetEmployeesIdDriverWallet,
  useGetEmployeesIdWaiterWallet,
  useGetEmployeesWallets,
} from '@agos/api-client/hr'
import {
  useGetOrderTabs,
  useGetOrdersDailySummary,
  useGetSalesChannels,
} from '@agos/api-client/orders'
import {
  T,
  formatCents,
  formatDateTime,
  formatDay,
  parseMoneyToCents,
  toDateParam,
  tt,
  channelLabel,
  filterChannelsOf,
  type FilterChannel,
  OrderNumberLink,
} from '@agos/shared'
import {
  DataTable,
  EmptyState,
  FilterBar,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  cn,
  type Column, BUTTON_PRIMARY} from '@agos/ui'
import {
  AlertTriangle,
  BarChart3,
  Banknote,
  ChevronRight,
  CreditCard,
  Globe,
  MapPin,
  Receipt,
  Search,
  Tag,
  Truck,
  UserCheck,
  Wallet,
  X,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import { tabsOf, totalsOf } from '../order-tabs/order-tabs'

import {
  byRole,
  byWallet,
  channelLines,
  initialsOf,
  orderWord,
  reconcile,
  type DaySummary,
  type WalletEmployee,
} from './wallet-reconciliation'

/**
 * `/summaries` — a day's takings, and who is carrying them.
 *
 * Legacy's layout: the hero total with the average order, the payment-method cards,
 * and the waiter and driver wallet tables that open a per-employee detail modal.
 *
 * **FB-075** is what it does not do. The hero and the wallets are two answers to the
 * same question from the same rows, and on 28 July in the restored data they differ
 * by 42,85 € — orders with no waiter and no driver on them. A manager reading a
 * wallet table under a headline total assumes the rows account for it. Now the page
 * says how much they do not, and in which payment methods.
 *
 * Also added: the channel split the backend has sent since §10A.1 and this page has
 * never read.
 *
 * Ported from legacy `pages/summaries.tsx`.
 */

const TYPE_COLORS: Record<string, 'blue' | 'green' | 'amber'> = {
  delivery: 'blue',
  restaurant: 'green',
  bar: 'amber',
}

const TYPE_LABELS: Record<string, string> = {
  delivery: tt('Rozvoz'),
  restaurant: tt('Reštaurácia'),
  bar: tt('Osobný odber'),
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta – terminál'),
  card_courier: tt('Karta – kuriér'),
  card_online: tt('CardPay Online'),
  card: tt('Karta'),
}

/**
 * §9.94 — the franchise's own name for the channel, with the hand-written map as the fallback for a
 * slug the API has not returned yet. This read the map alone, so a renamed channel kept its old
 * label and `na_sekeru` printed raw.
 */
function typeLabel(type: string | undefined, channels: readonly FilterChannel[]): string {
  if (!type) return ''
  return channelLabel(type, channels, TYPE_LABELS)
}

function paymentLabel(method: string | undefined): string {
  if (!method) return ''
  return PAYMENT_LABELS[method] ?? method
}

function today(): string {
  return toDateParam(new Date())
}

function yesterday(): string {
  const date = new Date()
  date.setDate(date.getDate() - 1)
  return toDateParam(date)
}

export function SummariesPage() {
  const [picked, setPicked] = useState(today)
  const [loaded, setLoaded] = useState(today)
  const [openDriverId, setOpenDriverId] = useState<number | null>(null)
  const [openWaiterId, setOpenWaiterId] = useState<number | null>(null)

  const { data: summaryData, isFetching: summaryFetching } = useGetOrdersDailySummary({
    date: loaded,
  })
  const { data: walletData, isLoading: walletsLoading } = useGetEmployeesWallets({
    dateFrom: loaded,
    dateTo: loaded,
  })

  const summary = summaryData as DaySummary | undefined
  const wallets = useMemo<WalletEmployee[]>(() => walletData ?? [], [walletData])

  // Read, not computed: `/order-tabs` is the open ones, already filtered by the server — the same
  // source `/revenue-handovers` uses, so the two pages cannot drift apart about it.
  const openTabs = useGetOrderTabs()
  const openTabsCents = totalsOf(tabsOf(openTabs.data)).outstandingCents

  const waiters = useMemo(() => byWallet(byRole(wallets, 'waiter')), [wallets])
  const drivers = useMemo(() => byWallet(byRole(wallets, 'driver')), [wallets])
  const check = reconcile(summary, wallets)
  const channels = channelLines(summary)

  const orderCount = summary?.orderCount ?? 0

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Prehľady')}
        description={tt('Denné tržby a peňaženky zamestnancov')}
        icon={<BarChart3 className="w-5 h-5" />}
      />

      {/* §9.5, the other half of what `/revenue-handovers` already says. `HandoverReconciliation`
        * filters `AND tab_id IS NULL`, so an open tab is not part of what anybody hands over — and
        * `WalletService`, which builds these wallets, does **not** filter it. So a wallet and a
        * handover differ by exactly the open tabs, and this page is where both are read. */}
      {openTabsCents > 0 && (
        <p className="text-[11px] text-muted-foreground" data-open-tabs>
          <T>Na otvorených účtoch je</T> <strong>{formatCents(openTabsCents)}</strong>{' '}
          <T>
            - je to v peňaženkách nižšie, ale nie v tom, čo sa odovzdáva. Sedieť to začne až po
            vyrovnaní účtu.
          </T>
        </p>
      )}

      <FilterBar
        filters={
          <>
            <input
              type="date"
              value={picked}
              onChange={(event) => setPicked(event.target.value)}
              aria-label={tt('Dátum')}
              className="px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            />
            <button
              type="button"
              onClick={() => setLoaded(picked)}
              disabled={summaryFetching || !picked}
              className={cn('flex items-center gap-2 px-5 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {summaryFetching ? <Spinner className="w-4 h-4" /> : <Search className="w-4 h-4" />}
              <T>Načítať</T>
            </button>
            <span className="text-sm text-muted-foreground">
              <T>Zobrazené:</T>{' '}
              <strong className="text-foreground" data-loaded-date>
                {dayLabel(loaded)}
              </strong>
            </span>
          </>
        }
        actions={
          <div className="flex gap-2 flex-wrap">
            {[
              { label: tt('Dnes'), date: today() },
              { label: tt('Včera'), date: yesterday() },
            ].map((shortcut) => (
              <button
                key={shortcut.label}
                type="button"
                onClick={() => {
                  setPicked(shortcut.date)
                  setLoaded(shortcut.date)
                }}
                className={cn(
                  'px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all',
                  loaded === shortcut.date
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-muted-foreground',
                )}
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        }
      />

      {summaryFetching && !summary ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : !summary ? (
        <div className="py-16">
          <EmptyState
            icon={<BarChart3 className="w-10 h-10" />}
            description={tt('Vyberte dátum a kliknite Načítať.')}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4" data-day-summary>
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between min-w-[220px]">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">
                  <T>Celková tržba</T>
                </p>
                <p className="text-5xl font-black leading-none tracking-tight tabular-nums">
                  {formatCents(check.dayCents)}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {orderCount} {orderWord(orderCount)}
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                  <T>Priemerná objednávka</T>
                </p>
                <p className="text-xl font-semibold tabular-nums">
                  {orderCount > 0 ? formatCents(parseMoneyToCents(summary.avgOrder)) : '---'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard
                label={tt('Hotovosť')}
                value={formatCents(parseMoneyToCents(summary.cashTotal))}
                icon={Banknote}
                color="emerald"
              />
              <StatCard
                label={tt('Karta')}
                value={formatCents(parseMoneyToCents(summary.cardTerminalTotal))}
                icon={CreditCard}
                color="blue"
              />
              <StatCard
                label={tt('Kuriér karta')}
                value={formatCents(parseMoneyToCents(summary.cardCourierTotal))}
                icon={CreditCard}
                color="orange"
              />
              <StatCard
                label={tt('Online')}
                value={formatCents(parseMoneyToCents(summary.cardOnlineTotal))}
                icon={Globe}
                color="violet"
              />
              <StatCard
                label="Doprava"
                value={formatCents(parseMoneyToCents(summary.totalDelivery))}
                icon={Truck}
                color="primary"
              />
              <StatCard
                label={tt('Zľavy')}
                value={formatCents(parseMoneyToCents(summary.totalDiscount))}
                icon={Tag}
                color={parseMoneyToCents(summary.totalDiscount) > 0 ? 'amber' : 'primary'}
              />
              <StatCard
                label="Storno"
                value={`${summary.cancelledCount ?? 0} obj.`}
                sub={
                  (summary.cancelledCount ?? 0) > 0
                    ? formatCents(parseMoneyToCents(summary.cancelledTotal))
                    : undefined
                }
                icon={Receipt}
                color={(summary.cancelledCount ?? 0) > 0 ? 'red' : 'primary'}
              />
            </div>
          </div>

          {/* Not in legacy: the backend has sent this since §10A.1. */}
          {channels.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5" data-channel-split>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                <T>Podľa predajného kanála</T>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {channels.map((channel) => (
                  <div key={channel.name} className="bg-muted/40 rounded-xl p-3">
                    <p className="text-sm font-semibold truncate">{channel.name}</p>
                    <p className="text-lg font-bold tabular-nums">{formatCents(channel.cents)}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {channel.orderCount} {orderWord(channel.orderCount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-black flex items-center gap-2">
                <Wallet className="w-5 h-5 text-primary" />
                <T>Peňaženky zamestnancov</T>
              </h3>
            </div>

            {/* FB-075 - the hero and these rows answer the same question from the
                same orders, and they do not always agree. */}
            {!walletsLoading && !check.reconciles && (
              <div
                className="px-6 py-3 bg-amber-500/10 border-b border-amber-500/30 text-xs text-amber-500 flex items-start gap-2"
                data-unattributed
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  <T>Peňaženky nepokrývajú celú tržbu dňa. Bez zamestnanca je</T>{' '}
                  <strong>{formatCents(check.unattributedCents)}</strong>
                  {check.gaps.length > 0 && (
                    <>
                      {' - '}
                      {check.gaps
                        .map((gap) => `${gap.label} ${formatCents(gap.cents)}`)
                        .join(', ')}
                    </>
                  )}
                  . <T>Sú to objednávky, na ktorých nie je čašník ani šofér.</T>
                </span>
              </div>
            )}

            {walletsLoading ? (
              <div className="py-8 flex items-center justify-center">
                <Spinner className="w-5 h-5" />
              </div>
            ) : waiters.length === 0 && drivers.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                <T>Za toto obdobie žiadni zamestnanci nemajú objednávky.</T>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {waiters.length > 0 && (
                  <WalletSection
                    icon={<UserCheck className="w-3.5 h-3.5" />}
                    title={tt('Čašníci')}
                    wallets={waiters}
                    onOpen={setOpenWaiterId}
                    testId="waiters"
                  />
                )}
                {drivers.length > 0 && (
                  <WalletSection
                    icon={<Truck className="w-3.5 h-3.5" />}
                    title={tt('Šoféri')}
                    wallets={drivers}
                    onOpen={setOpenDriverId}
                    testId="drivers"
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}

      {openDriverId !== null && (
        <DriverDetail
          driverId={openDriverId}
          date={loaded}
          onClose={() => setOpenDriverId(null)}
        />
      )}
      {openWaiterId !== null && (
        <WaiterDetail
          waiterId={openWaiterId}
          date={loaded}
          onClose={() => setOpenWaiterId(null)}
        />
      )}
    </div>
  )
}

function WalletSection({
  icon,
  title,
  wallets,
  onOpen,
  testId,
}: {
  icon: ReactNode
  title: string
  wallets: WalletEmployee[]
  onOpen: (id: number) => void
  testId: string
}) {
  const columns: Column<WalletEmployee>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Meno'),
        sortable: true,
        sortValue: (employee) => employee.name,
        render: (employee) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {initialsOf(employee.name)}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                {employee.name}
                {employee.isExternal && <StatusBadge label={tt('Externý')} color="orange" />}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {employee.orderCount ?? 0} {orderWord(employee.orderCount ?? 0)}
              </p>
            </div>
          </div>
        ),
      },
      moneyColumn('walletTotal', tt('Celková tržba'), (employee) => employee.walletTotal, 'strong'),
      moneyColumn('cashTotal', tt('Hotovosť'), (employee) => employee.cashTotal, 'emerald'),
      moneyColumn('cardTerminalTotal', tt('Terminál'), (employee) => employee.cardTerminalTotal, 'blue'),
      moneyColumn('cardCourierTotal', tt('Kuriér'), (employee) => employee.cardCourierTotal, 'orange'),
      moneyColumn('cardOnlineTotal', 'CardPay', (employee) => employee.cardOnlineTotal, 'violet'),
      {
        key: 'action',
        header: '',
        render: () => <ChevronRight className="w-4 h-4 text-muted-foreground" />,
      },
    ],
    [],
  )

  return (
    <div className="p-5" data-wallet-section={testId}>
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-1.5">
        {icon} {title}
        <span className="ml-1 text-[10px] font-normal">
          (<T>klikni pre detail</T>)
        </span>
      </p>
      <DataTable<WalletEmployee>
        columns={columns}
        data={wallets}
        rowKey={(employee) => employee.id ?? 0}
        onRowClick={(employee) => employee.id !== undefined && onOpen(employee.id)}
        pageSize={10}
        compact
        emptyMessage={tt('Žiadni zamestnanci')}
      />
    </div>
  )
}

const MONEY_TONES = {
  strong: 'text-base font-black',
  emerald: 'text-xs font-bold text-emerald-500',
  blue: 'text-xs font-bold text-blue-400',
  orange: 'text-xs font-bold text-orange-400',
  violet: 'text-xs font-bold text-violet-400',
} as const

function moneyColumn(
  key: string,
  header: string,
  pick: (employee: WalletEmployee) => WalletEmployee['walletTotal'],
  tone: keyof typeof MONEY_TONES,
): Column<WalletEmployee> {
  return {
    key,
    header,
    sortable: true,
    sortValue: (employee) => parseMoneyToCents(pick(employee)),
    className: 'text-right',
    headerClassName: 'text-right',
    render: (employee) => (
      <span className={cn('tabular-nums', MONEY_TONES[tone])}>
        {formatCents(parseMoneyToCents(pick(employee)))}
      </span>
    ),
  }
}

// ─── the per-employee detail ─────────────────────────────────────────────────

interface WalletOrder {
  id?: number
  orderNumber?: string
  type?: string
  status?: string
  customerName?: string | null
  customerAddress?: string | null
  tableName?: string | null
  total?: number | string
  paymentMethod?: string
  createdAt?: string
  durationMinutes?: number | null
}

interface WalletDetailSummary {
  totalRevenue?: number | string
  orderCount?: number
  cashTotal?: number | string
  cardTotal?: number | string
  cardTerminalTotal?: number | string
  cardCourierTotal?: number | string
  cardOnlineTotal?: number | string
}

interface WalletGroup {
  address?: string
  tableName?: string
  count?: number
  total?: number | string
}

function DriverDetail({
  driverId,
  date,
  onClose,
}: {
  driverId: number
  date: string
  onClose: () => void
}) {
  const { data, isLoading } = useGetEmployeesIdDriverWallet(driverId, {
    dateFrom: date,
    dateTo: date,
  })
  const detail = data as
    | { employee?: WalletEmployee; summary?: WalletDetailSummary; orders?: WalletOrder[]; addresses?: WalletGroup[] }
    | undefined

  return (
    <WalletModal
      loading={isLoading}
      name={detail?.employee?.name}
      role={tt('Šofér')}
      date={date}
      summary={detail?.summary}
      orders={detail?.orders ?? []}
      groups={detail?.addresses ?? []}
      groupTab="Adresy"
      groupHeader={tt('Adresa')}
      groupOf={(group) => group.address ?? ''}
      onClose={onClose}
    />
  )
}

function WaiterDetail({
  waiterId,
  date,
  onClose,
}: {
  waiterId: number
  date: string
  onClose: () => void
}) {
  const { data, isLoading } = useGetEmployeesIdWaiterWallet(waiterId, {
    dateFrom: date,
    dateTo: date,
  })
  const detail = data as
    | { employee?: WalletEmployee; summary?: WalletDetailSummary; orders?: WalletOrder[]; tables?: WalletGroup[] }
    | undefined

  return (
    <WalletModal
      loading={isLoading}
      name={detail?.employee?.name}
      role={tt('Čašník')}
      date={date}
      summary={detail?.summary}
      orders={detail?.orders ?? []}
      groups={detail?.tables ?? []}
      groupTab={tt('Stoly')}
      groupHeader={tt('Stôl')}
      groupOf={(group) => group.tableName ?? ''}
      onClose={onClose}
    />
  )
}

function WalletModal({
  loading,
  name,
  role,
  date,
  summary,
  orders,
  groups,
  groupTab,
  groupHeader,
  groupOf,
  onClose,
}: {
  loading: boolean
  name: string | undefined
  role: string
  date: string
  summary: WalletDetailSummary | undefined
  orders: WalletOrder[]
  groups: WalletGroup[]
  groupTab: string
  groupHeader: string
  groupOf: (group: WalletGroup) => string
  onClose: () => void
}) {
  // §9.94 — the franchise's own names for its channels, so a rename reaches this table.
  const { data: channelData } = useGetSalesChannels()
  const salesChannels = useMemo(() => filterChannelsOf(channelData), [channelData])

  const [tab, setTab] = useState<'orders' | 'groups'>('orders')

  const orderColumns: Column<WalletOrder>[] = useMemo(
    () => [
      {
        key: 'orderNumber',
        header: '#',
        sortable: true,
        sortValue: (order) => order.orderNumber,
        // §9.143 — the takings table is a record; each row's order opens in the archive.
        render: (order) => (
          <OrderNumberLink
            orderId={order.id}
            className="font-mono text-xs font-bold text-primary hover:underline"
            title={`${tt('Otvoriť v archíve')}: #${order.orderNumber}`}
          >
            #{order.orderNumber}
          </OrderNumberLink>
        ),
      },
      {
        key: 'type',
        header: 'Typ',
        sortable: true,
        sortValue: (order) => typeLabel(order.type, salesChannels),
        render: (order) => (
          <StatusBadge
            label={typeLabel(order.type, salesChannels)}
            color={TYPE_COLORS[order.type ?? ''] ?? 'zinc'}
          />
        ),
      },
      {
        key: 'customer',
        header: tt('Zákazník'),
        sortable: true,
        sortValue: (order) => order.customerName ?? '',
        render: (order) => (
          <div className="min-w-0">
            {order.customerName && (
              <p className="text-sm font-semibold truncate">{order.customerName}</p>
            )}
            {order.customerAddress && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {order.customerAddress}
              </p>
            )}
            {order.tableName && (
              <p className="text-xs text-muted-foreground truncate">{order.tableName}</p>
            )}
          </div>
        ),
      },
      {
        key: 'payment',
        header: tt('Platba'),
        sortable: true,
        sortValue: (order) => paymentLabel(order.paymentMethod),
        render: (order) => (
          <span className="text-xs text-muted-foreground">{paymentLabel(order.paymentMethod)}</span>
        ),
      },
      {
        key: 'createdAt',
        header: tt('Dátum'),
        sortable: true,
        sortValue: (order) => new Date(order.createdAt ?? 0).getTime(),
        render: (order) => (
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            {order.createdAt ? formatDateTime(order.createdAt) : ''}
            {order.durationMinutes != null && ` · ${Math.round(order.durationMinutes)} min`}
          </p>
        ),
      },
      {
        key: 'total',
        header: 'Suma',
        sortable: true,
        sortValue: (order) => parseMoneyToCents(order.total),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (order) => (
          <span className="font-bold text-sm whitespace-nowrap tabular-nums">
            {formatCents(parseMoneyToCents(order.total))}
          </span>
        ),
      },
    ],
    // §9.94 — the columns read the franchise's channel names, so they rebuild when those arrive.
    [salesChannels],
  )

  const groupColumns: Column<WalletGroup>[] = useMemo(
    () => [
      {
        key: 'label',
        header: groupHeader,
        sortable: true,
        sortValue: (group) => groupOf(group),
        render: (group) => (
          <p className="text-sm font-semibold truncate flex items-center gap-1">
            <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
            {groupOf(group)}
          </p>
        ),
      },
      {
        key: 'count',
        header: tt('Návštevy'),
        sortable: true,
        sortValue: (group) => group.count ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (group) => (
          <span className="text-xs font-bold text-primary whitespace-nowrap">
            {group.count ?? 0}×
          </span>
        ),
      },
      {
        key: 'total',
        header: 'Suma',
        sortable: true,
        sortValue: (group) => parseMoneyToCents(group.total),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (group) => (
          <span className="text-sm font-semibold tabular-nums">
            {formatCents(parseMoneyToCents(group.total))}
          </span>
        ),
      },
    ],
    [groupHeader, groupOf],
  )

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${name ?? role} - ${tt('detail peňaženky')}`}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-wallet-detail
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border shrink-0">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary text-sm font-bold">
            {initialsOf(name) || '?'}
          </div>
          <div>
            <p className="font-bold">{loading ? tt('Načítavam...') : name}</p>
            <p className="text-xs text-muted-foreground">
              {role} · {dayLabel(date)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="ml-auto p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : !summary ? (
          <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground text-sm">
            <T>Dáta nie sú dostupné</T>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-border">
              <StatCard
                label="Celkovo"
                value={formatCents(parseMoneyToCents(summary.totalRevenue))}
                color="primary"
              />
              <StatCard label={tt('Objednávok')} value={String(summary.orderCount ?? 0)} color="primary" />
              <StatCard
                label={tt('Hotovosť')}
                value={formatCents(parseMoneyToCents(summary.cashTotal))}
                icon={Banknote}
                color="emerald"
              />
              <StatCard
                label={tt('Terminál')}
                value={formatCents(parseMoneyToCents(summary.cardTerminalTotal))}
                icon={CreditCard}
                color="blue"
              />
              <StatCard
                label={tt('Kuriér karta')}
                value={formatCents(parseMoneyToCents(summary.cardCourierTotal))}
                icon={CreditCard}
                color="orange"
              />
              <StatCard
                label="CardPay"
                value={formatCents(parseMoneyToCents(summary.cardOnlineTotal))}
                icon={Globe}
                color="violet"
              />
              <StatCard
                label={tt('Karta spolu')}
                value={formatCents(parseMoneyToCents(summary.cardTotal))}
                icon={CreditCard}
                color="primary"
              />
            </div>

            <div className="px-5 pt-4">
              <TabGroup
                tabs={[
                  { id: 'orders', label: tt('Objednávky'), count: orders.length },
                  { id: 'groups', label: groupTab, count: groups.length },
                ]}
                active={tab}
                onChange={(id) => setTab(id as 'orders' | 'groups')}
                className="mb-0"
              />
            </div>

            {tab === 'orders' ? (
              <div className="p-5">
                <DataTable<WalletOrder>
                  columns={orderColumns}
                  data={orders}
                  rowKey={(order) => order.id ?? 0}
                  pageSize={10}
                  compact
                  emptyMessage={tt('Žiadne objednávky')}
                />
              </div>
            ) : (
              <div className="p-5">
                <DataTable<WalletGroup>
                  columns={groupColumns}
                  data={groups}
                  rowKey={(_, index) => index}
                  pageSize={10}
                  compact
                  emptyMessage={tt('Žiadne záznamy')}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** `Dnes`, `Včera`, or the date — legacy's own labelling. */
function dayLabel(date: string): string {
  if (date === today()) return tt('Dnes')
  if (date === yesterday()) return tt('Včera')
  return formatDay(`${date}T12:00:00`)
}
